import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { DEFAULT_CONTENT_WIDTH } from '../../../../../constants';
import { Tabs } from './components/tabs';
import { HousesGrid } from './components/houses-grid';
import { useAvailableHouses } from './hooks/use-available-houses';
import { useComingSoonHouses } from './hooks/use-coming-soon-houses';
import { TabType } from './types';
import { useLocale } from '@/hooks/use-locale';
import { IconSaxIcon } from '@/components/design-system/atoms/icon';

const FindYourNextHomeSection: React.FC = () => {
  const { t } = useTranslation();
  const { currentLocale } = useLocale();
  const [activeTab, setActiveTab] = useState<TabType>('available');

  const { data: availableData, isLoading: availableLoading } = useAvailableHouses(9);

  const { data: comingSoonData, isLoading: comingSoonLoading } = useComingSoonHouses(6);

  const currentHouses =
    activeTab === 'available'
      ? availableData?.colocations || []
      : comingSoonData?.colocations || [];

  const isLoading = activeTab === 'available' ? availableLoading : comingSoonLoading;

  const handleExploreAllClick = () => {
    window.location.href = `/${currentLocale}/shared-houses`;
  };

  return (
    <section className="w-full bg-white py-10">
      <div className={clsx(DEFAULT_CONTENT_WIDTH, 'm-auto')}>
        {/* Title */}
        <div className={clsx('flex items-center justify-between', 'w-full', 'mb-7')}>
          <h1
            className={clsx(
              'font-bold text-text-primary',
              'text-h3 sm:text-[4.8rem]',
              'leading-[3.7rem] sm:leading-[5.5rem]'
            )}
          >
            {t('js.features.marketplace.home.find_your_next_home_section.title')}
          </h1>
        </div>

        {/* Tabs and Explore All Button Row */}
        <div className={clsx('flex justify-between', 'mb-5', 'min-w-0', 'overflow-hidden')}>
          <Tabs activeTab={activeTab} onTabChange={setActiveTab} />
          <button
            onClick={handleExploreAllClick}
            className={clsx(
              'items-center justify-center transition-colors',
              'hidden lg:flex',
              'gap-2',
              'font-medium sm:font-bold',
              'text-[16px] leading-[22px]',
              'text-text-primaryHeavy hover:text-text-secondary'
            )}
          >
            {t('js.features.marketplace.home.find_your_next_home_section.explore_all_houses')}
            <IconSaxIcon name="arrow-right" size={20} strokeColor="currentColor" />
          </button>
        </div>

        {/* Houses Grid */}
        <HousesGrid
          houses={currentHouses}
          isComingSoon={activeTab === 'coming-soon'}
          isLoading={isLoading}
        />

        {/* View All Button (Bottom) */}
        <div className={clsx('flex justify-center', 'mt-5')}>
          <button
            onClick={handleExploreAllClick}
            className={clsx(
              'rounded transition-colors',
              'px-3',
              'py-3',
              'font-medium',
              'text-lead2',
              'bg-N200 hover:bg-N300',
              'text-N900'
            )}
          >
            {t('js.features.marketplace.home.find_your_next_home_section.view_all')}
          </button>
        </div>
      </div>
    </section>
  );
};

export { FindYourNextHomeSection };
